import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Plus,
  Trash2,
  Loader2,
  Mail,
  Shield,
  Crown,
  User,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import OrganisationTypePicker, { ENTITY_TYPES } from '../components/OrganisationTypePicker';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [teamData, setTeamData] = useState({ members: [], count: 0, max_members: 5 });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [copiedId, setCopiedId] = useState(null);
  const [orgType, setOrgType] = useState(null);
  const [selectedOrgType, setSelectedOrgType] = useState(null);
  const [savingOrgType, setSavingOrgType] = useState(false);

  useEffect(() => {
    fetchTeamData();
    fetchOrgType();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const data = await api.getTeamMembers();
      setTeamData(data);
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgType = async () => {
    try {
      const data = await api.getOrganisationType();
      setOrgType(data.organisation_type || null);
      setSelectedOrgType(data.organisation_type || 'charity');
    } catch (error) {
      // Non-fatal; entity features may not be configured yet
    }
  };

  const handleSaveOrgType = async () => {
    if (!selectedOrgType) return;
    try {
      setSavingOrgType(true);
      await api.setOrganisationType(selectedOrgType);
      setOrgType(selectedOrgType);
      toast.success('Organisation type saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save organisation type');
    } finally {
      setSavingOrgType(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setInviting(true);
      await api.inviteTeamMember(inviteEmail, inviteRole);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');
      fetchTeamData();
    } catch (error) {
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId, email) => {
    if (!confirm(`Are you sure you want to remove ${email} from the team?`)) {
      return;
    }

    try {
      await api.removeTeamMember(memberId);
      toast.success('Team member removed');
      fetchTeamData();
    } catch (error) {
      toast.error(error.message || 'Failed to remove member');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="text-black" size={16} />;
      case 'admin':
        return <Shield className="text-black" size={16} />;
      default:
        return <User className="text-ink/50" size={16} />;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-lime border border-ink/10 rounded-lg',
      admin: 'bg-ink text-white',
      member: 'bg-white border border-ink/10 rounded-lg'
    };
    return styles[role] || styles.member;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin" size={32} />
          <p className="font-bold uppercase tracking-wide">Loading settings...</p>
        </div>
      </div>
    );
  }

  const currentUserRole = teamData.members.find(m => m.email === user?.email)?.role || 'member';
  const canManageTeam = currentUserRole === 'owner' || currentUserRole === 'admin';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm mb-4 hover:underline decoration-4 underline-offset-4 decoration-[#bcd445]"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        <h1 className="font-bold tracking-tight text-4xl mb-2">Settings</h1>
        <p className="font-medium text-ink/70">Manage your team and organization settings</p>
      </div>

      {/* Team Management */}
      <div className="mb-6 bg-white border border-ink/10 rounded-lg shadow-saas">
        <div className="p-6 border-b border-ink/10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold tracking-tight text-2xl flex items-center gap-2">
                <Users size={24} />
                Team Members
              </h2>
              <p className="text-sm font-bold uppercase tracking-wide text-ink/60 mt-1">
                {teamData.count} of {teamData.max_members} members
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {/* Team Member List */}
          <div className="space-y-3 mb-6">
            {teamData.members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-cream border border-ink/10 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white border border-ink/10 rounded-lg flex items-center justify-center">
                    {getRoleIcon(member.role)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{member.email}</p>
                      {member.email === user?.email && (
                        <span className="text-xs font-bold uppercase text-ink/50">(you)</span>
                      )}
                    </div>
                    <span className={`inline-block text-xs font-bold uppercase px-2 py-0.5 ${getRoleBadge(member.role)}`}>
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </span>
                  </div>
                </div>

                {canManageTeam && member.role !== 'owner' && member.email !== user?.email && (
                  <button
                    onClick={() => handleRemoveMember(member.id, member.email)}
                    className="p-2 bg-red-500 text-white border border-ink/10 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Invite Form */}
          {canManageTeam && teamData.count < teamData.max_members && (
            <div className="border-t border-ink/10 pt-6">
              <h3 className="font-bold tracking-tight text-lg mb-4 flex items-center gap-2">
                <Plus size={18} />
                Invite Team Member
              </h3>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="invite-email" className="font-bold uppercase text-sm tracking-wide">Email Address</label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" size={16} />
                      <input
                        id="invite-email"
                        type="email"
                        placeholder="colleague@organisation.org"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-cream border border-ink/10 rounded-lg font-medium focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="invite-role" className="font-bold uppercase text-sm tracking-wide">Role</label>
                    <select
                      id="invite-role"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="w-full mt-1 px-4 py-3 bg-cream border border-ink/10 rounded-lg font-medium focus:outline-none focus:bg-lime/20"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={inviting || !inviteEmail.trim()}
                  className="inline-flex items-center px-6 py-3 bg-lime border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
                >
                  {inviting ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={16} className="mr-2" />
                      Send Invitation
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Limit Reached */}
          {teamData.count >= teamData.max_members && (
            <div className="border-t border-ink/10 pt-6">
              <div className="bg-lime border border-ink/10 rounded-lg p-4 text-center">
                <p className="font-bold uppercase tracking-wide">Team member limit reached</p>
                <p className="font-medium text-sm mt-1">
                  Your plan allows up to {teamData.max_members} team members
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Organization Info */}
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas">
        <div className="p-6 border-b border-ink/10">
          <h2 className="font-bold tracking-tight text-2xl">Organization</h2>
          <p className="text-sm font-bold uppercase tracking-wide text-ink/60 mt-1">Your organization details</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="font-bold uppercase text-sm tracking-wide text-ink/60">Organization Name</label>
              <p className="font-bold">{user?.org_name || 'Your Organization'}</p>
            </div>
            <div>
              <label className="font-bold uppercase text-sm tracking-wide text-ink/60">Your Role</label>
              <p className="font-bold flex items-center gap-2">
                {getRoleIcon(currentUserRole)}
                {currentUserRole.charAt(0).toUpperCase() + currentUserRole.slice(1)}
              </p>
            </div>
            <div>
              <label className="font-bold uppercase text-sm tracking-wide text-ink/60">Account Email</label>
              <p className="font-bold">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Organisation Type (entity governance) */}
      <div className="mt-6 bg-white border border-ink/10 rounded-lg shadow-saas">
        <div className="p-6 border-b border-ink/10">
          <h2 className="font-bold tracking-tight text-2xl">Organisation Type</h2>
          <p className="text-sm font-bold uppercase tracking-wide text-ink/60 mt-1">
            Drives your entity review reference library, document checklist and questions
          </p>
        </div>
        <div className="p-6">
          {orgType && (
            <div className="mb-4 bg-cream border border-ink/10 rounded-lg p-3 inline-flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-ink/60">Current:</span>
              <span className="font-bold">
                {ENTITY_TYPES.find((t) => t.value === orgType)?.label || orgType}
              </span>
            </div>
          )}
          <OrganisationTypePicker
            value={selectedOrgType}
            onChange={setSelectedOrgType}
            columns={4}
          />
          <button
            onClick={handleSaveOrgType}
            disabled={savingOrgType || selectedOrgType === orgType}
            className="mt-5 inline-flex items-center px-6 py-3 bg-lime border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
          >
            {savingOrgType ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Saving...
              </>
            ) : (
              'Save Organisation Type'
            )}
          </button>
          {orgType && selectedOrgType !== orgType && (
            <p className="mt-3 text-xs font-medium text-ink/60">
              Changing your organisation type updates the reference library and assessment questions for new reviews.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
